import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { TECHNOLOGY, AGNOSTIC_STACK, IMAGES } from "@/data/content";
import { PageHero } from "@/components/shared/PageHero";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { Reveal, Stagger, Item } from "@/components/shared/Reveal";
import { Icon } from "@/components/shared/Icon";
import { Architecture } from "@/components/home/Architecture";
import { Partners } from "@/components/technology/Partners";
import { ClosingCTA } from "@/components/home/ClosingCTA";

const DETAIL = {
  "IoT & Telematics": ["GPS / GNSS tracking units", "CAN-bus, OBD and J1939 interfaces", "Fuel, temperature, door and load sensors", "Asset and trailer trackers", "Driver identification devices"],
  "Video & CCTV": ["Mobile DVR (MDVR) systems", "IP, analogue and AI dash cameras", "Driver-, road- and cabin-facing cameras", "Live streaming and remote playback", "Video analytics and event upload"],
  "Cloud Platforms": ["Fleet management platforms", "Logistics and delivery management", "School transport management", "Video management platforms", "Dashboards, reporting and mobile apps"],
  "AI": ["Computer vision on the edge and in the cloud", "Driver behaviour and risk models", "Predictive maintenance analytics", "Anomaly detection", "Operational intelligence and reporting"],
  "Connectivity": ["4G / 5G cellular", "IoT SIMs and data plans", "Depot Wi-Fi offload", "Device and SIM management", "Cloud connectivity and security"],
  "Integrations": ["REST APIs and webhooks", "ERP and finance systems", "E-commerce and marketplaces", "HR, payroll and school systems", "Custom middleware and software"],
};

export default function Technology() {
  useSEO({ title: "Technology Ecosystem | IoT, Telematics, CCTV, Cloud, AI & Integrations | Wexoz", description: "The Wexoz technology ecosystem: IoT and telematics, video and CCTV, cloud transportation platforms, AI, connectivity and enterprise integrations — selected and integrated for each operation." });
  return (
    <>
      <PageHero eyebrow="Technology" lines={["An ecosystem,", "not a product list."]} image={IMAGES.controlRoom}
        text="Wexoz works across six technology categories and selects from many vendors and platforms. What matters is how they are combined for your operation." />

      <section className="section">
        <div className="wrap">
          <SectionHeading eyebrow="Technology ecosystem" title="Hardware · Software · Connectivity · AI · Integrations" />
          <Stagger className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {TECHNOLOGY.map((t, i) => (
              <Item key={t.title} data-testid={`technology-card-${i + 1}`} className="card group flex flex-col p-8">
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-line bg-navy text-cyan-400"><Icon name={t.icon} className="h-5 w-5" /></span>
                  <span className="font-mono text-xs text-slate-500">0{i + 1}</span>
                </div>
                <h3 className="mt-8 text-2xl font-semibold">{t.title}</h3>
                <p className="mt-3 text-sm text-slate-400">{t.text}</p>
                <ul className="mt-6 space-y-2 border-t border-line pt-6">
                  {DETAIL[t.title].map((d) => <li key={d} className="flex items-start gap-2 text-sm text-slate-300"><span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-cyan-400" />{d}</li>)}
                </ul>
              </Item>
            ))}
          </Stagger>
        </div>
      </section>

      <Partners />

      <Architecture />

      <section className="section bg-[#F4F6FA] text-navy">
        <div className="wrap grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading light eyebrow="Powered by the right technology" title="Proven platforms, integrated where they fit." text="Wexoz can deploy and integrate established transportation technology platforms as part of a broader solution." />
            <Reveal delay={0.1} className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-600">Platform selection</div>
              <p className="mt-3 text-sm leading-relaxed text-slate-700">Wexoz selects from established fleet, logistics, school transport, video and AI platforms as part of a broader transportation technology solution. Depending on the customer's requirements, Wexoz can also integrate other platforms, hardware and technologies.</p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">We can deploy and integrate</div>
            <Stagger className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" stagger={0.04}>
              {AGNOSTIC_STACK.map((t) => (
                <Item key={t} className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-navy shadow-sm">{t}</Item>
              ))}
            </Stagger>
            <Link to="/contact?solution=Systems%20Integration" data-testid="technology-integration-cta" className="btn-primary mt-10">Discuss an integration <ArrowUpRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>
      <ClosingCTA />
    </>
  );
}
